import React, { memo, useCallback, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { RenderComponent } from './SiteRenderer';
import AnimatedComponentWrapper from './AnimatedComponentWrapper';
import { SectionAnimationProvider } from './SectionAnimationContext';
import {
  forestFillGridPlacement,
  getSectionDesignKit,
  gridRowHeightForSection,
  isForestFillImage,
  isJardinsForestSection,
  kitGridTrackStyle,
  kitGridItemSizeStyle,
  kitGridZIndex,
} from '../utils/designKit';
import {
  resolveJardinsSectionPadding,
  shouldUseJardinsCompactHeader,
} from '../utils/jardinsResponsive';
import JardinsCompactHeader from './JardinsCompactHeader';
import '../styles/WebCreatorJardinsKit.css';
import SectionBackground from './SectionBackground';
import AlignmentGuides from './AlignmentGuides';
import {
  getComponentDisplayName,
  getGridPlacement,
  getComponentLayout,
  gridStyle,
  hasCustomViewportLayout,
  sortComponentsByGrid,
  componentStackStyle,
  getEditorPlaneVisual,
  isComponentHidden,
  isSectionHidden,
  VIEWPORTS,
  componentOverlapsOthers,
  GRID_COLUMNS,
  GRID_ROW_HEIGHT,
} from '../utils/defaults';
import {
  resolveSectionFixedHeightPx,
  resolveSectionTotalGridRows,
} from '../utils/sectionGrid';
import { resolveSectionBackgroundOverlayStyle } from '../utils/sectionOverlay';
import { getCommerceCapabilities } from '../utils/commerce';
import { useCommerceSectionExpandRegistry } from '../utils/commerceSectionExpand';

const RESIZE_HANDLES = [
  { id: 'e', className: 'top-1/2 -right-1 -translate-y-1/2 w-2 h-6 cursor-ew-resize' },
  { id: 's', className: '-bottom-1 left-1/2 -translate-x-1/2 w-6 h-2 cursor-ns-resize' },
  { id: 'se', className: '-bottom-1 -right-1 w-3 h-3 cursor-nwse-resize' },
];

const EDITOR_BLOCK_LABEL_CLASS =
  'wc-editor-block-label absolute left-1 z-30 max-w-[min(100%-0.5rem,18rem)] truncate pointer-events-none text-[10px] leading-tight font-semibold px-2 py-0.5 rounded-md border border-white/30 bg-slate-900 text-white shadow-[0_1px_3px_rgba(0,0,0,0.45)]';

const GridComponent = memo(function GridComponent({
  comp,
  sections,
  section,
  sectionId,
  sectionComponents,
  viewport,
  siteTypography,
  site,
  selected,
  interacting,
  livePreview,
  ghost = false,
  faded = false,
  planeVisual,
  stackable = false,
  onSelect,
  onOpenSettings,
  onDelete,
  onDuplicate,
  onBringToFront,
  onSendToBack,
  onStartMove,
  onStartResize,
  suppressClickRef,
  totalGridRows = 0,
  totalCommerceExtraPx = 0,
  kitRowH = GRID_ROW_HEIGHT,
}) {
  const forestBleedImage = isForestFillImage(comp, section);
  const rawPlacement =
    interacting && livePreview ? livePreview : getGridPlacement(comp, viewport);
  const placement =
    forestBleedImage && totalGridRows > 0 && viewport === 'desktop'
      ? forestFillGridPlacement(rawPlacement, totalGridRows)
      : rawPlacement;
  const isCustom = hasCustomViewportLayout(comp, viewport);
  const rowSpan = placement.rowSpan ?? 1;
  const hubupType = comp.type === 'hubupQr' || comp.type === 'hubupNotifications';
  const commerceEmbedded =
    (comp.type === 'clickAndCollect' || comp.type === 'reservation') &&
    comp.props?.displayMode === 'inline';
  const commerceExpanded = commerceEmbedded && totalCommerceExtraPx > 0;
  const commerceSlotHeightPx = (rowSpan ?? 1) * kitRowH;
  const fullPageRestaurant =
    comp.type === 'restaurantPage' || comp.type === 'restaurantSection';
  const hidden = isComponentHidden(comp, viewport);
  const planeInactive = planeVisual?.planeInactive === true;
  const blockLabel = getComponentDisplayName(comp);

  return (
    <div
      data-grid-component
      data-ghost={ghost ? 'true' : undefined}
      data-plane-inactive={planeInactive ? 'true' : undefined}
      className={
        [
          selected && !ghost && !interacting ? 'overflow-visible' : '',
          planeInactive ? 'wc-plane-inactive pointer-events-none' : '',
          section?.designKit
            ? `wc-kit-grid-item wc-kit-grid-item--${comp.type}${
                comp.props?.kitStyle ? ` wc-kit-grid-item--kit-${comp.props.kitStyle}` : ''
              } min-h-0`
            : '',
        ]
          .filter(Boolean)
          .join(' ') || undefined
      }
      style={{
        ...gridStyle(placement, interacting),
        ...(planeVisual || componentStackStyle(comp, { selected: selected && !ghost, interacting: interacting && !ghost })),
        ...(section?.designKit
          ? { zIndex: kitGridZIndex(comp, getComponentLayout(comp)) }
          : {}),
        ...(fullPageRestaurant
          ? {}
          : kitGridItemSizeStyle(section, comp, rowSpan, kitRowH, { hubupType, viewport })),
        ...(fullPageRestaurant ? { overflow: 'visible', width: '100%' } : {}),
        ...(ghost ? { pointerEvents: 'none', opacity: 0.55, outline: '2px dashed hsl(var(--primary))' } : {}),
        ...(faded ? { opacity: 0.25, pointerEvents: 'none' } : {}),
        ...(planeVisual?.planeInactive && !ghost && !faded
          ? {
              opacity: planeVisual.opacity,
              pointerEvents: planeVisual.pointerEvents,
            }
          : {}),
        ...(hidden && !ghost && !planeInactive ? { opacity: 0.35, outline: '2px dashed hsl(var(--muted-foreground) / 0.45)' } : {}),
        ...(commerceExpanded
          ? { overflow: 'visible', maxHeight: 'none', height: 'auto' }
          : commerceEmbedded
            ? { overflow: 'hidden', maxHeight: '100%' }
            : {}),
        ...(selected && !ghost && !interacting && !hubupType && !commerceEmbedded
          ? { overflow: 'visible' }
          : {}),
      }}
    >
      {selected && !interacting && !ghost && !planeInactive && (
        <div
          data-editor-control
          className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-40 flex items-center gap-1 bg-card/95 backdrop-blur-sm border border-border rounded-lg px-1 py-0.5 shadow-lg pointer-events-auto whitespace-nowrap"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted text-xs"
            title="Modifier"
            onClick={(e) => {
              e.stopPropagation();
              onOpenSettings({ kind: 'component', sectionId, component: comp });
            }}
          >
            <Icon name="Settings" size={13} />
            <span>Modifier</span>
          </button>
          <button
            type="button"
            className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted text-xs"
            title="Dupliquer (Ctrl+D)"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate(sectionId, comp.id);
            }}
          >
            <Icon name="Copy" size={13} />
            <span>Dupliquer</span>
          </button>
          {stackable ? (
            <>
              <span className="w-px h-5 bg-border shrink-0" aria-hidden />
              <button
                type="button"
                className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted text-xs"
                title="Premier plan"
                onClick={(e) => {
                  e.stopPropagation();
                  onBringToFront?.(sectionId, comp.id);
                }}
              >
                <Icon name="BringToFront" size={13} />
                <span>Premier plan</span>
              </button>
              <button
                type="button"
                className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted text-xs"
                title="Arrière-plan"
                onClick={(e) => {
                  e.stopPropagation();
                  onSendToBack?.(sectionId, comp.id);
                }}
              >
                <Icon name="SendToBack" size={13} />
                <span>Arrière-plan</span>
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-destructive/10 text-destructive text-xs"
            title="Supprimer"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(sectionId, comp.id);
            }}
          >
            <Icon name="Trash2" size={13} />
            <span>Supprimer</span>
          </button>
        </div>
      )}

      {!ghost && !planeInactive ? (
        <span
          className={`${EDITOR_BLOCK_LABEL_CLASS} ${isCustom ? 'top-7' : 'top-1'}`}
          title={blockLabel}
        >
          {blockLabel}
        </span>
      ) : null}

      {isCustom && !ghost && (
        <span className="absolute top-1 left-1 z-[31] text-[9px] px-1.5 py-0.5 rounded-md border border-white/25 bg-amber-600 text-white font-semibold shadow-[0_1px_3px_rgba(0,0,0,0.4)] pointer-events-none">
          custom
        </span>
      )}

      {hidden && !ghost && (
        <span className="absolute top-1 right-1 z-20 text-[9px] px-1.5 py-0.5 rounded-full bg-muted-foreground/80 text-background font-medium shadow-sm pointer-events-none">
          Masqué
        </span>
      )}

      {Array.isArray(comp.viewports) &&
      comp.viewports.length > 0 &&
      comp.viewports.length < 3 &&
      !ghost ? (
        <span className="absolute bottom-1 left-1 z-20 text-[9px] px-1.5 py-0.5 rounded-full bg-violet-600/90 text-white font-medium shadow-sm pointer-events-none max-w-[90%] truncate">
          {comp.viewports.map((id) => VIEWPORTS[id]?.label || id).join(' · ')}
        </span>
      ) : null}

      <div
        className={
          section?.designKit
            ? [
                'relative h-full min-h-0 rounded-none select-none',
                interacting || ghost ? 'touch-none' : '',
                hubupType || commerceEmbedded ? 'overflow-hidden' : 'overflow-visible',
                ghost
                  ? 'wc-kit-editor-surface wc-kit-editor-surface--ghost'
                  : selected || interacting
                    ? 'wc-kit-editor-surface wc-kit-editor-surface--active'
                    : 'wc-kit-editor-surface',
              ]
                .filter(Boolean)
                .join(' ')
            : [
                `relative h-full ${commerceEmbedded ? 'min-h-0' : 'min-h-full'} rounded-none select-none ${
                  interacting || ghost ? 'touch-none' : ''
                }`,
                hubupType || commerceEmbedded ? 'overflow-hidden' : 'overflow-visible',
                ghost
                  ? 'ring-2 ring-inset ring-primary/40 bg-primary/[0.06]'
                  : selected || interacting
                    ? 'ring-2 ring-inset ring-primary shadow-lg shadow-primary/10 bg-primary/[0.03]'
                    : 'ring-1 ring-inset ring-primary/20 hover:ring-primary/40 hover:bg-primary/[0.02]',
                interacting && !ghost
                  ? 'scale-[1.01] opacity-95'
                  : 'transition-[box-shadow,transform,opacity] duration-200',
              ].join(' ')
        }
        onPointerDown={(e) => {
          if (ghost || planeInactive) return;
          if (e.target.closest('[data-editor-control]')) return;
          if (forestBleedImage) {
            e.stopPropagation();
            onSelect(comp.id, sectionId);
            return;
          }
          onStartMove(sectionId, comp, e);
        }}
        onClick={(e) => {
          if (ghost || planeInactive) return;
          e.stopPropagation();
          if (suppressClickRef?.current) return;
          onSelect(comp.id, sectionId);
        }}
        onDoubleClick={(e) => {
          if (ghost || planeInactive) return;
          e.stopPropagation();
          e.preventDefault();
          onSelect(comp.id, sectionId);
          onOpenSettings({ kind: 'component', sectionId, component: comp });
        }}
      >
        <div
          className={`${
            commerceExpanded
              ? 'min-h-0 overflow-visible h-auto'
              : 'h-full min-h-0 max-h-full'
          } ${
            commerceEmbedded ? 'pointer-events-auto' : 'pointer-events-none'
          } ${commerceEmbedded && !commerceExpanded ? 'overflow-hidden' : ''} ${
            section?.designKit ? 'min-h-0' : commerceEmbedded ? 'min-h-0' : 'min-h-full'
          }`}
        >
          <AnimatedComponentWrapper
            comp={comp}
            section={section}
            viewport={viewport}
            disabled
          >
            <RenderComponent
              comp={comp}
              sections={sections}
              interactive={false}
              editorViewport={viewport}
              siteTypography={siteTypography}
              liveHubupFetch
              site={site}
              checkoutSlug={site?.slug}
              commerceCapabilities={site ? getCommerceCapabilities(site) : undefined}
              commerceSlotHeightPx={commerceEmbedded ? commerceSlotHeightPx : 0}
            />
          </AnimatedComponentWrapper>
        </div>

        {selected && !interacting && !ghost && !planeInactive && !forestBleedImage && (
          <>
            {RESIZE_HANDLES.map((h) => (
              <div
                key={h.id}
                data-editor-control
                className={`absolute z-30 rounded-full bg-primary border-2 border-background shadow-md ${h.className}`}
                onPointerDown={(e) => onStartResize(sectionId, comp, h.id, e)}
              />
            ))}
          </>
        )}

        {interacting && !ghost && !forestBleedImage && (
          <>
            {RESIZE_HANDLES.map((h) => (
              <div
                key={h.id}
                data-editor-control
                className={`absolute z-30 rounded-full bg-primary border-2 border-background shadow-md ${h.className}`}
                onPointerDown={(e) => onStartResize(sectionId, comp, h.id, e)}
              />
            ))}
          </>
        )}

        {selected && !ghost && forestBleedImage ? (
          <p className="absolute bottom-1 left-1 right-1 z-30 pointer-events-none text-center text-[9px] text-primary/80 bg-background/90 px-1 py-0.5 rounded">
            Photo liée à la hauteur de section — poignée bleue en bas
          </p>
        ) : null}
      </div>
    </div>
  );
});

function EditorSection({
  section,
  sections,
  viewport,
  siteTypography,
  site,
  selectedSectionId,
  selectedComponentId,
  dragSession,
  registerGrid,
  suppressClickRef,
  onStartMove,
  onStartResize,
  onSelectSection,
  onSelectComponent,
  onOpenSettings,
  onDeleteComponent,
  onDeleteSection,
  onDuplicateComponent,
  onResizeSection,
  onBringToFront,
  onSendToBack,
  planeFilter,
  jardinsFluidScale = 1,
}) {
  const [resizePreviewRows, setResizePreviewRows] = useState(null);
  const isSelected = selectedSectionId === section.id;
  const sorted = useMemo(
    () => sortComponentsByGrid(section.components, viewport),
    [section.components, viewport],
  );

  const gridCallbackRef = useCallback(
    (el) => {
      registerGrid(section.id, el);
    },
    [registerGrid, section.id],
  );

  const activeId = dragSession?.comp?.id ?? null;
  const isInteracting = dragSession?.isInteracting ?? false;
  const preview = dragSession?.preview ?? null;

  const isIncomingTarget =
    dragSession?.mode === 'move' &&
    dragSession.targetSectionId === section.id &&
    dragSession.fromSectionId !== section.id &&
    dragSession.preview;

  const isDragSourceSection =
    dragSession?.mode === 'move' &&
    dragSession.fromSectionId === section.id &&
    dragSession.targetSectionId !== section.id;

  const isDropHighlight = dragSession?.isInteracting && dragSession.targetSectionId === section.id;

  const kitRowH =
    (gridRowHeightForSection(section) || GRID_ROW_HEIGHT) *
    (section?.designKit === 'jardins' && viewport === 'desktop' ? jardinsFluidScale : 1);
  const forestSection = isJardinsForestSection(section);
  const forestDesktopBleed = forestSection && viewport === 'desktop';
  const incomingPreviewEnd = isIncomingTarget
    ? dragSession.preview.row + (dragSession.preview.rowSpan ?? 1) - 1
    : 0;

  const sectionRowsOptions = {
    resizePreviewRows,
    previewCompId: activeId,
    previewPlacement: activeId && preview ? preview : null,
    incomingPreviewEnd,
  };

  const totalGridRows = resolveSectionTotalGridRows(
    section,
    viewport,
    section.components,
    sectionRowsOptions,
  );

  const userFixedHeightPx = resolveSectionFixedHeightPx(
    section,
    viewport,
    totalGridRows,
    kitRowH,
  );
  const forestGridHeightPx = forestDesktopBleed ? totalGridRows * kitRowH : null;
  const sectionGridHeightPx =
    resizePreviewRows != null
      ? totalGridRows * kitRowH
      : userFixedHeightPx ?? forestGridHeightPx;

  const { totalExtraPx, CommerceSectionExpandProvider } = useCommerceSectionExpandRegistry();
  const baseSectionHeightPx =
    sectionGridHeightPx ?? (totalGridRows > 0 ? totalGridRows * kitRowH : null);
  const expandedSectionHeightPx =
    baseSectionHeightPx != null && totalExtraPx > 0
      ? baseSectionHeightPx + totalExtraPx
      : baseSectionHeightPx;

  const isRestaurantLanding =
    sorted.length > 0 &&
    sorted.every((c) => c.type === 'restaurantPage' || c.type === 'restaurantSection');

  const sectionKit = getSectionDesignKit(section);
  const sectionTone = section.kitSectionTone
    ? `wc-kit-section--${section.kitSectionTone}`
    : '';
  const sectionHiddenOnViewport = isSectionHidden(section, viewport);

  const startSectionResize = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const startY = e.clientY;
    const startRows = totalGridRows;

    const minRows = 4;

    const onMove = (ev) => {
      const deltaRows = Math.round((ev.clientY - startY) / kitRowH);
      setResizePreviewRows(Math.max(minRows, startRows + deltaRows));
    };

    const onUp = (ev) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const deltaRows = Math.round((ev.clientY - startY) / kitRowH);
      const nextViewportRows = Math.max(minRows, startRows + deltaRows);
      setResizePreviewRows(null);
      onResizeSection?.(section.id, nextViewportRows, viewport);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };


  if (sectionHiddenOnViewport) {
    return (
      <section
        id={section.anchor}
        className={`w-full relative group/section border-2 border-dashed border-muted-foreground/35 bg-muted/20 ${
          isSelected ? 'ring-2 ring-inset ring-primary/40' : ''
        }`}
        style={{ padding: '16px 20px' }}
        onClick={(e) => {
          e.stopPropagation();
          onSelectSection(section.id);
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{section.label}</span>
            {' — masquée sur '}
            {viewport === 'mobile' ? 'téléphone' : 'tablette'}
          </p>
          <button
            type="button"
            className="p-1.5 rounded-full bg-card/95 border border-border shadow-sm hover:bg-card shrink-0"
            title="Paramètres section"
            onClick={(e) => {
              e.stopPropagation();
              onOpenSettings({ kind: 'section', sectionId: section.id, section });
            }}
          >
            <Icon name="Settings" size={13} />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      id={section.anchor}
      className={`w-full relative group/section transition-all duration-200 ${sectionKit?.sectionClass || ''} ${sectionTone} ${
        isDropHighlight
          ? 'ring-2 ring-inset ring-primary/60 bg-primary/[0.03]'
          : isSelected
            ? 'ring-2 ring-inset ring-primary/40'
            : 'hover:ring-1 hover:ring-inset hover:ring-primary/15'
      }`.trim()}
      style={{
        paddingTop:
          resolveJardinsSectionPadding(section, viewport) ??
          (isRestaurantLanding || section.designKit ? (section.paddingY ?? 0) : section.paddingY ?? 64),
        paddingBottom:
          resolveJardinsSectionPadding(section, viewport) ??
          (isRestaurantLanding || section.designKit ? (section.paddingY ?? 0) : section.paddingY ?? 64),
      }}
      onClick={(e) => {
        if (e.target.closest('[data-grid-component]')) return;
        onSelectSection(section.id);
      }}
    >
      <SectionBackground section={section} editor />
      {section.kitSectionTone === 'hero-dark' &&
      !resolveSectionBackgroundOverlayStyle(section) ? (
        <div className="wc-kit-hero-shade absolute inset-0 z-[1]" aria-hidden />
      ) : null}
      <div
        className={`absolute top-3 right-3 z-20 flex items-center gap-1 transition-opacity ${
          isSelected ? 'opacity-100' : 'opacity-0 group-hover/section:opacity-100'
        }`}
      >
        <span className="text-[10px] font-semibold px-2.5 py-1 rounded-md border border-white/30 bg-slate-900 text-white shadow-[0_1px_3px_rgba(0,0,0,0.45)]">
          {section.label}
        </span>
        <button
          type="button"
          className="p-1.5 rounded-full bg-card/95 border border-border shadow-sm hover:bg-card"
          title="Paramètres section"
          onClick={(e) => {
            e.stopPropagation();
            onOpenSettings({ kind: 'section', sectionId: section.id, section });
          }}
        >
          <Icon name="Settings" size={13} />
        </button>
        <button
          type="button"
          className="p-1.5 rounded-full bg-card/95 border border-border shadow-sm hover:bg-card text-destructive"
          title="Supprimer"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteSection(section.id);
          }}
        >
          <Icon name="Trash2" size={13} />
        </button>
      </div>

      <SectionAnimationProvider section={section} disabled={false}>
      <div className="relative z-[2]">
      {shouldUseJardinsCompactHeader(section, viewport) ? (
        <JardinsCompactHeader
          section={section}
          interactive
          checkoutSlug={site?.slug}
          editorMode={{ onSelect: onSelectComponent }}
        />
      ) : (
      <CommerceSectionExpandProvider>
      <div
        ref={gridCallbackRef}
        data-section-grid={section.id}
        data-grid-row-height={kitRowH}
        data-commerce-section-expanded={totalExtraPx > 0 ? 'true' : undefined}
        className={`w-full grid gap-0 relative overflow-visible ${sectionKit ? 'wc-kit-section-grid' : ''}`}
        style={{
          gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, 1fr))`,
          ...kitGridTrackStyle(section, totalGridRows, kitRowH, viewport, {
            forceFixedRowHeight: resizePreviewRows != null,
          }),
          ...(expandedSectionHeightPx != null
            ? {
                minHeight: expandedSectionHeightPx,
                height: totalExtraPx > 0 ? 'auto' : expandedSectionHeightPx,
              }
            : {
                minHeight:
                  sorted.length === 0 && !isIncomingTarget ? totalGridRows * kitRowH : undefined,
              }),
        }}
      >
        {sorted.map((comp) => {
          const isActive = activeId === comp.id;
          const faded = isDragSourceSection && isActive;
          const planeVisual = getEditorPlaneVisual(comp, planeFilter, section.components, {
            selected: selectedComponentId === comp.id,
            interacting: isActive && isInteracting && dragSession?.targetSectionId === section.id,
            viewport,
          });
          const interacting =
            isActive && isInteracting && dragSession?.targetSectionId === section.id;
          return (
            <GridComponent
              key={comp.id}
              comp={comp}
              sections={sections}
              section={section}
              sectionId={section.id}
              sectionComponents={section.components}
              viewport={viewport}
              siteTypography={siteTypography}
              site={site}
              selected={selectedComponentId === comp.id}
              interacting={interacting}
              livePreview={isActive ? preview : null}
              faded={faded}
              planeVisual={planeVisual}
              stackable={componentOverlapsOthers(comp, section.components, viewport)}
              onSelect={onSelectComponent}
              onOpenSettings={onOpenSettings}
              onDelete={onDeleteComponent}
              onDuplicate={onDuplicateComponent}
              onBringToFront={onBringToFront}
              onSendToBack={onSendToBack}
              onStartMove={onStartMove}
              onStartResize={onStartResize}
              suppressClickRef={suppressClickRef}
              totalGridRows={totalGridRows}
              totalCommerceExtraPx={totalExtraPx}
              kitRowH={kitRowH}
            />
          );
        })}

        {isIncomingTarget && dragSession.comp ? (
          <GridComponent
            key={`ghost-${dragSession.comp.id}`}
            comp={dragSession.comp}
            sections={sections}
            section={section}
            sectionId={section.id}
            viewport={viewport}
            siteTypography={siteTypography}
            site={site}
            ghost
            interacting
            livePreview={dragSession.preview}
            onSelect={() => {}}
            onOpenSettings={() => {}}
            onDelete={() => {}}
            onDuplicate={() => {}}
            onStartMove={() => {}}
            onStartResize={() => {}}
          />
        ) : null}

        {sorted.length === 0 && !isIncomingTarget && (
          <div
            className="flex items-center justify-center text-sm text-muted-foreground/80 border border-dashed border-primary/25 bg-primary/[0.02]"
            style={{ gridColumn: '1 / -1', minHeight: totalGridRows * kitRowH }}
          >
            Ajoutez un composant depuis le panneau à droite — glissez ici depuis une autre section
          </div>
        )}

        {isInteracting &&
        dragSession?.targetSectionId === section.id &&
        Array.isArray(dragSession?.guides) &&
        dragSession.guides.length > 0 ? (
          <div
            className="pointer-events-none relative z-[40]"
            style={{ gridColumn: '1 / -1', gridRow: '1 / -1' }}
          >
            <AlignmentGuides guides={dragSession.guides} gridRows={totalGridRows} rowHeight={kitRowH} />
          </div>
        ) : null}
      </div>
      </CommerceSectionExpandProvider>
      )}
      </div>
      </SectionAnimationProvider>

      {isSelected ? (
        <div className="absolute bottom-2 left-0 right-0 z-30 flex justify-center pointer-events-none">
          <div
            role="separator"
            aria-label="Redimensionner la hauteur de la section"
            title="Glisser pour ajuster la hauteur"
            className="pointer-events-auto h-2.5 w-28 rounded-full bg-primary/90 border-2 border-background shadow-md cursor-ns-resize hover:bg-primary"
            onPointerDown={startSectionResize}
          />
        </div>
      ) : null}
    </section>
  );
}

export default memo(EditorSection);
